<template>
  <div class="preferences-page">
    <AppSectionHero
      :title="t.title"
      :subtitle="t.subtitle"
      :stats="heroStats"
      :show-refresh="false"
    />

    <CRow>
      <CCol xl="8" class="mb-4">
        <CCard class="border-0 shadow-sm settings-shell">
          <CCardBody class="p-4">
            <div class="settings-shell__eyebrow">{{ t.workspaceSettings }}</div>
            <div class="settings-shell__title">{{ t.personalPreferences }}</div>
            <div class="settings-shell__copy">{{ t.personalPreferencesCopy }}</div>

            <CForm @submit.prevent="savePreferences">
              <div class="settings-section">
                <div class="settings-section__head">
                  <div class="settings-section__title">{{ t.sections.languageRegion }}</div>
                  <div class="settings-section__copy">{{ t.sections.languageRegionCopy }}</div>
                </div>

                <CRow>
                  <CCol md="6" class="mb-3">
                    <label class="settings-label">{{ t.fields.language }}</label>
                    <select v-model="form.language" class="form-control settings-control">
                      <option value="en">{{ t.languageEnglish }}</option>
                      <option value="th">{{ t.languageThai }}</option>
                    </select>
                  </CCol>
                  <CCol md="6" class="mb-3">
                    <label class="settings-label">{{ t.fields.timeFormat }}</label>
                    <select v-model="form.timeFormat" class="form-control settings-control">
                      <option value="24h">{{ t.time24 }}</option>
                      <option value="12h">{{ t.time12 }}</option>
                    </select>
                  </CCol>
                </CRow>
              </div>

              <div class="settings-section">
                <div class="settings-section__head">
                  <div class="settings-section__title">{{ t.sections.notifications }}</div>
                  <div class="settings-section__copy">{{ t.sections.notificationsCopy }}</div>
                </div>

                <div class="toggle-list">
                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.emailNotificationsTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.emailNotificationsCopy }}</div>
                    </div>
                    <input v-model="form.emailNotifications" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.desktopNotificationsTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.desktopNotificationsCopy }}</div>
                    </div>
                    <input v-model="form.desktopNotifications" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.reviewAlertsTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.reviewAlertsCopy }}</div>
                    </div>
                    <input v-model="form.reviewAlerts" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.translationAlertsTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.translationAlertsCopy }}</div>
                    </div>
                    <input v-model="form.translationAlerts" type="checkbox">
                  </label>
                </div>
              </div>

              <div class="settings-section">
                <div class="settings-section__head">
                  <div class="settings-section__title">{{ t.sections.layoutNavigation }}</div>
                  <div class="settings-section__copy">{{ t.sections.layoutNavigationCopy }}</div>
                </div>

                <CRow>
                  <CCol md="6" class="mb-3">
                    <label class="settings-label">{{ t.fields.defaultLandingPage }}</label>
                    <select v-model="form.defaultLandingPage" class="form-control settings-control">
                      <option v-for="item in landingOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
                    </select>
                  </CCol>
                  <CCol md="6" class="mb-3">
                    <label class="settings-label">{{ t.fields.resultsPerPage }}</label>
                    <select v-model.number="form.itemsPerPage" class="form-control settings-control">
                      <option :value="10">10</option>
                      <option :value="20">20</option>
                      <option :value="50">50</option>
                      <option :value="100">100</option>
                    </select>
                  </CCol>
                </CRow>

                <div class="toggle-list">
                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.rememberSidebarTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.rememberSidebarCopy }}</div>
                    </div>
                    <input v-model="form.rememberSidebar" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.startMinimizedSidebarTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.startMinimizedSidebarCopy }}</div>
                    </div>
                    <input v-model="form.startMinimizedSidebar" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.keepNavigationContextTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.keepNavigationContextCopy }}</div>
                    </div>
                    <input v-model="form.keepNavigationContext" type="checkbox">
                  </label>
                </div>
              </div>

              <div class="settings-section">
                <div class="settings-section__head">
                  <div class="settings-section__title">{{ t.sections.privacySession }}</div>
                  <div class="settings-section__copy">{{ t.sections.privacySessionCopy }}</div>
                </div>

                <div class="toggle-list">
                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.rememberRecentPagesTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.rememberRecentPagesCopy }}</div>
                    </div>
                    <input v-model="form.rememberRecentPages" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.storeDraftRecoveryTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.storeDraftRecoveryCopy }}</div>
                    </div>
                    <input v-model="form.storeDraftRecovery" type="checkbox">
                  </label>

                  <label class="toggle-row">
                    <div>
                      <div class="toggle-row__title">{{ t.toggles.confirmBeforeSignOutTitle }}</div>
                      <div class="toggle-row__copy">{{ t.toggles.confirmBeforeSignOutCopy }}</div>
                    </div>
                    <input v-model="form.confirmBeforeSignOut" type="checkbox">
                  </label>
                </div>
              </div>

              <div class="settings-actions">
                <CButton color="primary" type="submit" class="mr-2">{{ t.save }}</CButton>
                <CButton color="secondary" variant="outline" type="button" @click="resetDefaults">{{ t.reset }}</CButton>
              </div>
            </CForm>
          </CCardBody>
        </CCard>
      </CCol>

      <CCol xl="4" class="mb-4">
        <CCard class="border-0 shadow-sm settings-panel mb-4">
          <CCardHeader class="bg-white border-0 pt-4 px-4">
            <div class="section-title">{{ t.currentState }}</div>
          </CCardHeader>
          <CCardBody class="pt-0 px-4 pb-4">
            <div class="summary-row"><strong>{{ t.fields.language }}</strong><span>{{ languageLabel }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.timeFormat }}</strong><span>{{ form.timeFormat === '24h' ? t.time24 : t.time12 }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.emailAlerts }}</strong><span>{{ yesNo(form.emailNotifications) }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.desktopAlerts }}</strong><span>{{ yesNo(form.desktopNotifications) }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.reviewAlerts }}</strong><span>{{ yesNo(form.reviewAlerts) }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.sidebarMemory }}</strong><span>{{ yesNo(form.rememberSidebar) }}</span></div>
            <div class="summary-row"><strong>{{ t.fields.landingPage }}</strong><span>{{ landingLabel }}</span></div>
          </CCardBody>
        </CCard>

        <CCard class="border-0 shadow-sm settings-panel mb-4">
          <CCardHeader class="bg-white border-0 pt-4 px-4">
            <div class="section-title">{{ t.recommendations }}</div>
          </CCardHeader>
          <CCardBody class="pt-0 px-4 pb-4">
            <div class="tip-card">
              <div class="tip-card__title">{{ t.tips.focusedReviewerTitle }}</div>
              <div class="tip-card__copy">{{ t.tips.focusedReviewerCopy }}</div>
            </div>
            <div class="tip-card">
              <div class="tip-card__title">{{ t.tips.frequentDrafterTitle }}</div>
              <div class="tip-card__copy">{{ t.tips.frequentDrafterCopy }}</div>
            </div>
            <div class="tip-card">
              <div class="tip-card__title">{{ t.tips.sharedDeviceTitle }}</div>
              <div class="tip-card__copy">{{ t.tips.sharedDeviceCopy }}</div>
            </div>
          </CCardBody>
        </CCard>

        <CCard class="border-0 shadow-sm settings-panel">
          <CCardHeader class="bg-white border-0 pt-4 px-4">
            <div class="section-title">{{ t.whatSavesHere }}</div>
          </CCardHeader>
          <CCardBody class="pt-0 px-4 pb-4">
            <div class="storage-note">{{ t.storageNote }}</div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import { getWorkspaceRole } from '@/utils/role-dashboard'
import {
  applyWorkspacePreferences,
  defaultWorkspacePreferences,
  readWorkspacePreferences,
  saveWorkspacePreferences
} from '@/utils/workspace-preferences'

export default {
  name: 'PreferencesPage',
  components: {
    AppSectionHero
  },
  data() {
    return {
      form: defaultWorkspacePreferences()
    }
  },
  computed: {
    t() {
      const messages = (this.$i18n && this.$i18n.messages && this.$i18n.locale)
        ? this.$i18n.messages[this.$i18n.locale]
        : {}
      return (messages && messages.preferences) || {}
    },
    workspaceRole() {
      return getWorkspaceRole(this.$store)
    },
    landingOptions() {
      if (this.workspaceRole === 'committee') {
        return [
          { value: '/dashboard/committee', label: this.t.landingOptions.committeeDashboard },
          { value: '/review?workspace=committee', label: this.t.landingOptions.review },
          { value: '/review-history?workspace=committee', label: this.t.landingOptions.reviewHistory },
          { value: '/guideline?workspace=committee', label: this.t.landingOptions.guideline }
        ]
      }
      return [
        { value: '/dashboard', label: this.t.landingOptions.dashboard },
        { value: '/dashboard/user-agreements', label: this.t.landingOptions.myAgreements },
        { value: '/dashboard/user-review-queue', label: this.t.landingOptions.myReviewQueue },
        { value: '/dashboard/user-translation', label: this.t.landingOptions.myTranslationRequests },
        { value: '/dashboard/user-reports', label: this.t.landingOptions.myReports }
      ]
    },
    heroStats() {
      return [
        { label: this.t.stats.alertsOn, value: this.enabledAlertCount, hint: this.t.stats.alertsOnHint, icon: 'cil-bell', iconClass: 'app-section-stat__icon--attention' },
        { label: this.t.stats.layout, value: this.form.startMinimizedSidebar ? this.t.compact : this.t.expanded, hint: this.t.stats.layoutHint, icon: 'cil-columns', iconClass: 'app-section-stat__icon--active' },
        { label: this.t.stats.privacy, value: this.privacyModeLabel, hint: this.t.stats.privacyHint, icon: 'cil-shield-alt', iconClass: 'app-section-stat__icon--total' }
      ]
    },
    enabledAlertCount() {
      return [
        this.form.emailNotifications,
        this.form.desktopNotifications,
        this.form.reviewAlerts,
        this.form.translationAlerts
      ].filter(Boolean).length
    },
    privacyModeLabel() {
      return this.form.rememberRecentPages && this.form.storeDraftRecovery ? this.t.standard : this.t.reduced
    },
    languageLabel() {
      return this.form.language === 'th' ? this.t.languageThai : this.t.languageEnglish
    },
    landingLabel() {
      const found = this.landingOptions.find(item => item.value === this.form.defaultLandingPage)
      return found ? found.label : this.form.defaultLandingPage
    }
  },
  mounted() {
    this.loadPreferences()
  },
  methods: {
    loadPreferences() {
      const preferences = readWorkspacePreferences()
      const fallbackLanding = this.landingOptions[0] ? this.landingOptions[0].value : '/dashboard'
      this.form = Object.assign(defaultWorkspacePreferences(), preferences, {
        defaultLandingPage: this.isValidLandingPage(preferences.defaultLandingPage) ? preferences.defaultLandingPage : fallbackLanding
      })
    },
    isValidLandingPage(value) {
      return this.landingOptions.some(item => item.value === value)
    },
    async ensureDesktopNotificationPermission() {
      if (!this.form.desktopNotifications || typeof Notification === 'undefined') return
      if (Notification.permission === 'default') {
        try {
          await Notification.requestPermission()
        } catch (error) {
          // Ignore permission prompt failures and keep the form saved.
        }
      }
    },
    persistUiPreferences() {
      applyWorkspacePreferences({ store: this.$store, i18nInstance: this.$i18n })
    },
    async savePreferences() {
      const fallbackLanding = this.landingOptions[0] ? this.landingOptions[0].value : '/dashboard'
      const nextForm = Object.assign({}, this.form, {
        defaultLandingPage: this.isValidLandingPage(this.form.defaultLandingPage) ? this.form.defaultLandingPage : fallbackLanding
      })
      saveWorkspacePreferences(nextForm)
      this.form = Object.assign(defaultWorkspacePreferences(), nextForm)
      await this.ensureDesktopNotificationPermission()
      this.persistUiPreferences()
      if (this.$store && this.$store.commit) {
        this.$store.commit('dialog/dialog', {
          title: 'Preferences Saved',
          message: 'Your local workspace preferences were updated successfully.',
          code: 'PREFERENCES_SAVED',
          number: '1',
          status: true,
          color: 'success',
          button: [
            {
              label: 'OK',
              icon: 'cil-check',
              color: 'success',
              code: 'ok'
            }
          ]
        })
      }
    },
    resetDefaults() {
      const fallbackLanding = this.landingOptions[0] ? this.landingOptions[0].value : '/dashboard'
      this.form = Object.assign(defaultWorkspacePreferences(), {
        defaultLandingPage: fallbackLanding
      })
      this.savePreferences()
    },
    yesNo(value) {
      return value ? this.t.enabled : this.t.disabled
    }
  }
}
</script>

<style scoped lang="scss">
.settings-shell,
.settings-panel {
  border-radius: 22px;
}

.settings-shell__eyebrow {
  color: #8c1515;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.settings-shell__title,
.section-title {
  color: #344767;
  font-size: 1.2rem;
  font-weight: 700;
}

.settings-shell__copy {
  color: #718096;
  line-height: 1.6;
  margin-top: 6px;
}

.settings-section {
  border-top: 1px solid #eef1f6;
  margin-top: 1.4rem;
  padding-top: 1.4rem;
}

.settings-section__head {
  margin-bottom: 1rem;
}

.settings-section__title {
  color: #344767;
  font-size: 1rem;
  font-weight: 700;
}

.settings-section__copy {
  color: #718096;
  font-size: 0.92rem;
  margin-top: 0.3rem;
}

.settings-label {
  color: #344767;
  display: block;
  font-size: 0.88rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
}

.settings-control {
  border-radius: 12px;
  min-height: 44px;
}

.toggle-list {
  display: grid;
  gap: 12px;
}

.toggle-row {
  align-items: center;
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #edf2f7;
  border-radius: 16px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin: 0;
  padding: 14px 16px;
}

.toggle-row__title {
  color: #344767;
  font-weight: 700;
}

.toggle-row__copy {
  color: #718096;
  font-size: 0.9rem;
  margin-top: 0.2rem;
}

.toggle-row input {
  height: 18px;
  width: 18px;
}

.settings-actions {
  border-top: 1px solid #eef1f6;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
}

.summary-row {
  align-items: center;
  border-top: 1px solid #eef1f6;
  color: #475569;
  display: flex;
  justify-content: space-between;
  padding: 1rem 0;
}

.summary-row strong {
  color: #344767;
  font-size: 0.92rem;
}

.summary-row span {
  color: #66758a;
  text-align: right;
}

.tip-card {
  background: linear-gradient(180deg, #fffefc 0%, #ffffff 100%);
  border: 1px solid #edf2f7;
  border-radius: 16px;
  margin-top: 12px;
  padding: 14px 16px;
}

.tip-card:first-child {
  margin-top: 0;
}

.tip-card__title {
  color: #344767;
  font-weight: 700;
}

.tip-card__copy,
.storage-note {
  color: #718096;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .toggle-row,
  .summary-row {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
